/**
 * Карточка расчёта: крупная сумма и её разбор.
 *
 * Главное здесь — итог: 34 пикселя, табличные цифры, ничего рядом. Человек
 * должен увидеть сумму раньше, чем прочитает что-либо ещё. Разбивка идёт
 * ниже и объясняет, откуда она взялась — так вопросов задают меньше.
 */

import clsx from 'clsx'

import {
  STATUS_LABELS,
  type Timesheet,
  type TimesheetStatus,
} from '@/shared/api/pay'
import { formatDuration } from '@/shared/format'
import { Card, Chip, Money, type ChipTone } from '@/shared/ui/primitives'

const STATUS_TONES: Record<TimesheetStatus, ChipTone> = {
  draft: 'neutral',
  sent: 'warm',
  paid_declared: 'accent',
  confirmed: 'success',
  disputed: 'danger',
}

export function TimesheetSummary({ timesheet }: { timesheet: Timesheet }) {
  const negative = Number(timesheet.amount_final) < 0

  return (
    <Card className="enter relative overflow-hidden">
      {/* тёплое пятно за суммой: итог периода — главная цифра приложения,
          и она должна светиться, а не лежать на ровной белой плашке */}
      <span
        aria-hidden
        className="bg-honey-soft pointer-events-none absolute -top-20 -left-16 size-56 rounded-full blur-3xl"
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-hand text-text-muted text-lead leading-tight">
            {timesheet.period_title}
          </p>
          <div className="mt-1.5">
            <Money
              value={timesheet.amount_final}
              currency={timesheet.currency}
              size="hero"
              tone={negative ? 'danger' : undefined}
            />
          </div>
          <p className="text-text-muted mt-1.5 text-note">
            {formatDuration(timesheet.minutes_total)}
            {timesheet.minutes_overtime > 0 &&
              ` · переработка ${formatDuration(timesheet.minutes_overtime)}`}
          </p>
        </div>
        <Chip tone={STATUS_TONES[timesheet.status]}>
          {timesheet.is_current && timesheet.status === 'draft'
            ? 'период идёт'
            : STATUS_LABELS[timesheet.status]}
        </Chip>
      </div>

      {negative && (
        <p className="text-text-muted mt-3 text-caption leading-snug">
          Выданные авансы превысили заработанное за период. Остаток перейдёт
          в следующий расчёт.
        </p>
      )}

      <div className="border-line relative mt-4 space-y-2 border-t pt-3">
        <Line
          label="Часы"
          value={timesheet.amount_base}
          currency={timesheet.currency}
          hint={formatDuration(timesheet.minutes_total - timesheet.minutes_overtime)}
        />
        {Number(timesheet.amount_overtime) !== 0 && (
          <Line
            label="Переработка"
            value={timesheet.amount_overtime}
            currency={timesheet.currency}
            hint={formatDuration(timesheet.minutes_overtime)}
          />
        )}
        {Number(timesheet.amount_expenses) !== 0 && (
          <Line
            label="Расходы"
            value={timesheet.amount_expenses}
            currency={timesheet.currency}
            hint={`${timesheet.expenses.length} шт.`}
          />
        )}
        {Number(timesheet.amount_adjustments) !== 0 && (
          <Line
            label="Корректировки"
            value={timesheet.amount_adjustments}
            currency={timesheet.currency}
          />
        )}
      </div>
    </Card>
  )
}

function Line({
  label,
  value,
  currency,
  hint,
}: {
  label: string
  value: string
  currency: string
  hint?: string
}) {
  const negative = Number(value) < 0
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-text-muted text-body">
        {label}
        {hint && <span className="ml-1.5 text-caption opacity-70">{hint}</span>}
      </span>
      <Money
        value={value}
        currency={currency}
        size="md"
        tone={negative ? 'muted' : undefined}
        className={clsx(negative && 'opacity-80')}
      />
    </div>
  )
}
